(() => {
  const elementName = 'pdp-v2-gallery';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2Gallery extends HTMLElement {
    connectedCallback() {
      this.retryCount = 0;
      this.setupFixedMobileGallery();
      this.setupScrollEffects();
      this.initialize();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      this.teardownProgressControls();
      this.fixedGalleryIntersectionObserver?.disconnect();
      this.scrollEffectsAbortController?.abort();
      window.cancelAnimationFrame(this.scrollEffectsFrame);
      this.scrollEffectsFrame = null;

      if (this.fixedGalleryMediaQuery && this.boundFixedGalleryBreakpointChange) {
        this.fixedGalleryMediaQuery.removeEventListener('change', this.boundFixedGalleryBreakpointChange);
      }

      this.fixedGalleryContainer?.removeAttribute('data-pdp-v2-fixed-gallery-hidden');
      this.fixedGalleryContainer?.style.removeProperty('--pdp-v2-gallery-overlay-opacity');
      this.scrollEffectsHeader?.removeAttribute('data-pdp-v2-sheet-past-header');

      if (this.boundUpdateSwiper) {
        window.removeEventListener('resize', this.boundUpdateSwiper);
      }

      if (this.imageRefreshHandlers) {
        this.imageRefreshHandlers.forEach(({ image, refresh }) => {
          image.removeEventListener('load', refresh);
          image.removeEventListener('error', refresh);
        });
        this.imageRefreshHandlers = null;
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    setupFixedMobileGallery() {
      this.fixedGalleryRoot = this.closest('.pdp-v2--mobile-layout-sticky-gallery-sheet');
      this.fixedGalleryContainer = this.closest('.pdp-v2__gallery');

      if (!this.fixedGalleryRoot || !this.fixedGalleryContainer) {
        return;
      }

      this.fixedGalleryIsIntersecting = true;
      this.fixedGalleryMediaQuery = window.matchMedia('(max-width: 749px)');
      this.boundFixedGalleryBreakpointChange = () => this.updateFixedGalleryVisibility();
      this.fixedGalleryMediaQuery.addEventListener('change', this.boundFixedGalleryBreakpointChange);

      if (window.IntersectionObserver) {
        this.fixedGalleryIntersectionObserver = new IntersectionObserver(([entry]) => {
          this.fixedGalleryIsIntersecting = entry.isIntersecting;
          this.updateFixedGalleryVisibility();
        });
        this.fixedGalleryIntersectionObserver.observe(this.fixedGalleryRoot);
      }

      this.updateFixedGalleryVisibility();
    }

    setupScrollEffects() {
      if (
        !this.fixedGalleryRoot ||
        !this.fixedGalleryContainer
      ) {
        return;
      }

      this.usesScrollOverlay = this.fixedGalleryRoot.dataset.pdpV2ScrollOverlay === 'true';
      this.usesTransparentHeader = this.fixedGalleryRoot.classList.contains(
        'pdp-v2--mobile-layout-sticky-gallery-sheet-transparent-header'
      );

      if (!this.usesScrollOverlay && !this.usesTransparentHeader) {
        return;
      }

      this.scrollEffectsSheet = this.fixedGalleryRoot.querySelector('.pdp-v2__info');
      this.scrollEffectsHeader = this.usesTransparentHeader
        ? document.querySelector('.shopify-section--header')
        : null;

      if (!this.scrollEffectsSheet) {
        return;
      }

      this.scrollEffectsAbortController = new AbortController();
      this.boundQueueScrollEffectsUpdate = () => this.queueScrollEffectsUpdate();

      window.addEventListener('scroll', this.boundQueueScrollEffectsUpdate, {
        passive: true,
        signal: this.scrollEffectsAbortController.signal,
      });
      window.addEventListener('resize', this.boundQueueScrollEffectsUpdate, {
        signal: this.scrollEffectsAbortController.signal,
      });

      this.updateScrollEffects();
    }

    queueScrollEffectsUpdate() {
      if (this.scrollEffectsFrame) {
        return;
      }

      this.scrollEffectsFrame = window.requestAnimationFrame(() => {
        this.scrollEffectsFrame = null;
        this.updateScrollEffects();
      });
    }

    updateScrollEffects() {
      if (
        !this.fixedGalleryMediaQuery?.matches ||
        !this.fixedGalleryContainer ||
        !this.scrollEffectsSheet
      ) {
        this.fixedGalleryContainer?.style.setProperty('--pdp-v2-gallery-overlay-opacity', '0');
        this.scrollEffectsHeader?.removeAttribute('data-pdp-v2-sheet-past-header');
        return;
      }

      const galleryRect = this.fixedGalleryContainer.getBoundingClientRect();
      const sheetRect = this.scrollEffectsSheet.getBoundingClientRect();
      const headerRect = this.scrollEffectsHeader?.getBoundingClientRect();
      const sheetMarginTop = Math.abs(parseFloat(window.getComputedStyle(this.scrollEffectsSheet).marginTop) || 0);
      const fadeStart = galleryRect.bottom - sheetMarginTop;
      const fadeEnd = headerRect?.bottom ?? galleryRect.top;
      const fadeDistance = Math.max(fadeStart - fadeEnd, 1);
      const progress = Math.min(Math.max((fadeStart - sheetRect.top) / fadeDistance, 0), 1);
      const opacity = this.usesScrollOverlay ? progress * 0.55 : 0;
      const opacityValue = opacity.toFixed(3);

      this.fixedGalleryContainer.style.setProperty('--pdp-v2-gallery-overlay-opacity', opacityValue);

      if (this.scrollEffectsHeader && headerRect) {
        this.scrollEffectsHeader.toggleAttribute(
          'data-pdp-v2-sheet-past-header',
          sheetRect.top <= headerRect.bottom
        );
      }
    }

    updateFixedGalleryVisibility() {
      if (!this.fixedGalleryContainer) {
        return;
      }

      const shouldHide = this.fixedGalleryMediaQuery?.matches && !this.fixedGalleryIsIntersecting;

      this.fixedGalleryContainer.toggleAttribute('data-pdp-v2-fixed-gallery-hidden', shouldHide);
      this.updateProgressPausedState();
    }

    initialize() {
      if (this.swiper) {
        return;
      }

      const swiperElement = this.querySelector('[data-pdp-v2-gallery-swiper]');
      const previousButton = this.querySelector('[data-pdp-v2-gallery-prev]');
      const nextButton = this.querySelector('[data-pdp-v2-gallery-next]');
      const pagination = this.querySelector('[data-pdp-v2-gallery-pagination]');

      if (!swiperElement) {
        return;
      }

      if (swiperElement.getBoundingClientRect().width <= 0) {
        this.retryWhenSwiperLoads();
        return;
      }

      const slideCount = swiperElement.querySelectorAll('.swiper-slide').length;
      const usesProgressControls = this.dataset.mobileCarouselControl?.includes('floating_progress');

      if (slideCount <= 1) {
        this.classList.add('pdp-v2-gallery--single');
      }

      if (swiperElement.swiper) {
        this.swiper = swiperElement.swiper;
        this.setupProgressControls();
        this.watchForLayoutUpdates(swiperElement);
        return;
      }

      if (!window.Swiper) {
        this.retryWhenSwiperLoads();
        return;
      }

      const paginationOptions = {
        el: pagination,
        clickable: true,
      };

      if (usesProgressControls) {
        paginationOptions.renderBullet = (index, className) => (
          `<button type="button" class="${className}" aria-label="Go to product media ${index + 1}"><span class="pdp-v2-gallery__progress-fill" data-pdp-v2-gallery-progress-fill aria-hidden="true"></span></button>`
        );
      }

      this.swiper = new window.Swiper(swiperElement, {
        slidesPerView: 1,
        speed: 500,
        watchOverflow: true,
        allowTouchMove: true,
        grabCursor: true,
        loop: false,
        rewind: true,
        autoplay: false,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        pagination: paginationOptions,
        navigation: {
          nextEl: nextButton,
          prevEl: previousButton,
          disabledClass: 'pdp-v2-gallery__button--disabled',
          lockClass: 'pdp-v2-gallery__button--locked',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          containerMessage: 'Product media gallery',
          prevSlideMessage: 'Previous product media',
          nextSlideMessage: 'Next product media',
          paginationBulletMessage: 'Go to product media {{index}}',
        },
      });

      this.setupProgressControls();
      this.watchForLayoutUpdates(swiperElement);
    }

    setupProgressControls() {
      if (this.progressControlsInitialized || !this.swiper) {
        return;
      }

      this.progressControls = this.querySelector('.pdp-v2-gallery__progress-controls');

      if (!this.progressControls) {
        return;
      }

      this.progressControlsInitialized = true;
      this.progressAbortController = new AbortController();
      this.progressMobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.progressIsVisible = true;
      this.boundProgressSlideChange = () => this.restartProgress();
      this.boundProgressVisibilityChange = () => this.updateProgressPausedState();
      this.boundProgressBreakpointChange = () => {
        this.updateProgressPausedState();
        this.restartProgress();
      };

      this.querySelector('[data-pdp-v2-gallery-progress-prev]')?.addEventListener('click', () => {
        this.swiper?.slidePrev();
      }, { signal: this.progressAbortController.signal });

      this.querySelector('[data-pdp-v2-gallery-progress-next]')?.addEventListener('click', () => {
        this.swiper?.slideNext();
      }, { signal: this.progressAbortController.signal });

      this.progressControls.addEventListener('animationend', (event) => {
        if (
          !event.target.matches('[data-pdp-v2-gallery-progress-fill]') ||
          event.animationName !== 'pdpV2GalleryProgress' ||
          this.hasAttribute('progress-paused')
        ) {
          return;
        }

        this.swiper?.slideNext();
      }, { signal: this.progressAbortController.signal });

      this.swiper.on('slideChange', this.boundProgressSlideChange);
      document.addEventListener('visibilitychange', this.boundProgressVisibilityChange, { signal: this.progressAbortController.signal });
      this.progressMobileMediaQuery.addEventListener('change', this.boundProgressBreakpointChange);

      if (window.IntersectionObserver) {
        this.progressIntersectionObserver = new IntersectionObserver(([entry]) => {
          this.progressIsVisible = entry.isIntersecting;
          this.updateProgressPausedState();
        });
        this.progressIntersectionObserver.observe(this);
      }

      this.restartProgress();
    }

    teardownProgressControls() {
      window.cancelAnimationFrame(this.progressRestartFrame);
      this.progressAbortController?.abort();
      this.progressIntersectionObserver?.disconnect();

      if (this.progressMobileMediaQuery && this.boundProgressBreakpointChange) {
        this.progressMobileMediaQuery.removeEventListener('change', this.boundProgressBreakpointChange);
      }

      if (this.swiper && this.boundProgressSlideChange) {
        this.swiper.off('slideChange', this.boundProgressSlideChange);
      }

      this.progressControlsInitialized = false;
    }

    restartProgress() {
      if (!this.progressControls || !this.swiper) {
        return;
      }

      window.cancelAnimationFrame(this.progressRestartFrame);
      this.querySelectorAll('[data-pdp-v2-gallery-progress-fill]').forEach((fill) => {
        fill.classList.remove('is-running');
      });
      this.progressFill = this.querySelectorAll('.pdp-v2-gallery__progress-dots .swiper-pagination-bullet')[this.swiper.realIndex]
        ?.querySelector('[data-pdp-v2-gallery-progress-fill]');

      if (!this.progressFill) {
        return;
      }

      if (
        Shopify.designMode ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !this.progressMobileMediaQuery.matches
      ) {
        this.updateProgressPausedState();
        return;
      }

      this.progressRestartFrame = window.requestAnimationFrame(() => {
        if (!this.isConnected) {
          return;
        }

        this.progressFill.getBoundingClientRect();
        this.progressFill.classList.add('is-running');
        this.updateProgressPausedState();
      });
    }

    updateProgressPausedState() {
      const shouldPause = (
        document.hidden ||
        !this.progressIsVisible ||
        !this.progressMobileMediaQuery?.matches ||
        (this.fixedGalleryMediaQuery?.matches && !this.fixedGalleryIsIntersecting)
      );

      this.toggleAttribute('progress-paused', shouldPause);
    }

    watchForLayoutUpdates(swiperElement) {
      const updateSwiper = () => {
        if (!this.swiper || this.swiper.destroyed) {
          return;
        }

        this.swiper.update();
      };

      if (this.boundUpdateSwiper) {
        window.removeEventListener('resize', this.boundUpdateSwiper);
      }
      this.boundUpdateSwiper = updateSwiper;
      window.addEventListener('resize', this.boundUpdateSwiper);

      if (this.imageRefreshHandlers) {
        this.imageRefreshHandlers.forEach(({ image, refresh }) => {
          image.removeEventListener('load', refresh);
          image.removeEventListener('error', refresh);
        });
      }

      this.imageRefreshHandlers = Array.from(swiperElement.querySelectorAll('img')).map((image) => {
        image.addEventListener('load', updateSwiper);
        image.addEventListener('error', updateSwiper);

        return { image, refresh: updateSwiper };
      });

      requestAnimationFrame(() => {
        updateSwiper();
        requestAnimationFrame(updateSwiper);
      });
    }

    retryWhenSwiperLoads() {
      window.clearTimeout(this.retryTimer);

      if (this.retryCount >= 60) {
        return;
      }

      this.retryCount += 1;
      this.retryTimer = window.setTimeout(() => this.initialize(), 100);
    }
  }

  customElements.define(elementName, PdpV2Gallery);
})();
